import { type ReactElement } from "react";
import { EllipsisVertical } from "@unified-checkout/design-system/icons/index";
import type {
  IActionBtnsTableCell,
  IBadgeTableCell,
  ICustomTableCell,
  IDropdownTableCell,
  ILeadingIconTableCell,
  ILeadingPaymentMethodTableCell,
  ITableCell,
  ITextTableCell,
} from "libs/design-system/src/types/table/table_row/table_cell";
import { FormatCurrencyForDisplay } from "libs/design-system/src/utils/FormatCurrencyForDisplay";
import { FeaturedIcon } from "../../FeaturedIcon";
import { Badge } from "../../Badge";
import { Button } from "../../Button";
import { Avatar } from "../../Avatar";
import { Dropdown } from "../../Dropdown";
import { DropdownButton } from "../../Dropdown/DropdownButton";
import { DropdownMenu } from "../../Dropdown/DropdownMenu";
import { DropdownMenuItem } from "../../Dropdown/DropdownMenuItem";
import styles from "./table-cell.module.scss";

const TableCellBase = (
  { text, hintText, avatar, classNameBase }
    : { text?: string, hintText?: string, avatar?: string, classNameBase: string }) => {
  const imageRegex = /^https:/;
  const isImage = imageRegex.test(avatar || "");

  return (
    <>
      {
        avatar &&
        <div className={styles[`${classNameBase}__avatar`]}>
          {
            isImage ?
              <Avatar
                size="md"
                type="img"
                imgUrl={avatar}
              />
              :
              <Avatar
                size="md"
                type="text"
                shortName={avatar.trim()
                  .split(/\s+/)
                  .slice(0, 2)
                  .map(word => word[0]?.toUpperCase())
                  .join('')}
              />
          }
        </div>
      }
      <div className={styles[`${classNameBase}__text-box`]}>
        {text && <div className={styles[`${classNameBase}__text-box__text`]}>{FormatCurrencyForDisplay(text)}</div>}
        {hintText && <div className={styles[`${classNameBase}__text-box__hint-text`]}>{hintText}</div>}
      </div>
    </>
  )
};

export const TableCell = (props: ITableCell) => {
  const { width, type, selected = false } = props;

  let content = <></>;

  if (type === 'text') {
    const { text, hintText, avatar } = props as ITextTableCell;

    content = <TableCellBase text={text} hintText={hintText} avatar={avatar} classNameBase="table-cell__content" />
  }

  else if (type === 'badge') {
    const { badgeProps, hintText } = props as IBadgeTableCell;

    content = <>
      {
        badgeProps &&
        <Badge
          size={badgeProps.size || "md"}
          type={badgeProps.type}
          color={badgeProps.color || "grey"}
          leadingIcon={badgeProps.leadingIcon}
          text={badgeProps.text}
          trailingIcon={badgeProps.trailingIcon}
        />
      }

      {hintText && <div className={styles["table-cell__content__hint-text"]}>{hintText}</div>}
    </>
  }

  else if (type === 'leading_icon') {
    const { text, hintText, icon } = props as ILeadingIconTableCell;
    content = <>
      {
        <FeaturedIcon
          icon={icon as ReactElement<'svg'>}
          color="gray"
          size="sm"
        />
      }
      <div className={styles["table-cell__content__text-box"]}>
        <TableCellBase text={text} hintText={hintText} classNameBase="table-cell__content__text-box" />
      </div>
    </>
  }

  else if (type === 'custom') {
    const { customContent } = props as ICustomTableCell;
    content = <>{customContent}</>;
  }

  else if (type === 'leading_payment_method') {
    const { paymentMethods } = props as ILeadingPaymentMethodTableCell;
    content = <>
      {
        <div className={styles["table-cell__content__icons-box"]}>
          {
            paymentMethods.filter((paymentMethod) => paymentMethod.type === "img").map((paymentMethod, index) =>
              <div
                className={styles["table-cell__content__icons-box__icon"]}
                key={`${`${paymentMethod.name}-` || ""}payment-method-${index}`}
              >
                <img
                  src={paymentMethod.url}
                  alt={paymentMethod.name || ""}
                />
              </div>
            )
          }
        </div>
      }
      {
        paymentMethods.filter((paymentMethod) => paymentMethod.type === "text").map((paymentMethod, index) =>
          <div
            className={styles["table-cell__content__text-box"]}
            key={`${`${paymentMethod.value}-` || ""}payment-method-${index}`}
          >
            <TableCellBase
              text={paymentMethod.value}
              classNameBase="table-cell__content__text-box"
            />
          </div>
        )
      }
    </>
  }

  else if (type === 'leading_text') {
    const { text, hintText, badgeProps } = props as any;
    content = (
      <>
        <div className={styles["table-cell__content__text-box"]}>
          <TableCellBase text={text} hintText={hintText} classNameBase="table-cell__content__text-box" />
        </div>
        {badgeProps && (
          <Badge
            size={badgeProps.size || 'md'}
            type={badgeProps.type}
            color={badgeProps.color}
            leadingIcon={badgeProps.leadingIcon}
            text={badgeProps.text}
            trailingIcon={badgeProps.trailingIcon}
          />
        )}
      </>
    );
  }

  else if (type === 'action_btns') {
    const { actionBtnsProps = [] } = props as IActionBtnsTableCell;
    content = <>
      {
        actionBtnsProps.map((btn, index) =>
          <Button
            key={`cell-action-btn-${index}`}
            size="md"
            {...btn}
          />
        )
      }
    </>
  }

  else if (type === 'dropdown') {
    const { dropdownMenuItemsProps, additionalData } = props as IDropdownTableCell;

    content = !!dropdownMenuItemsProps.length ? <div
      className={styles["table-cell__content__dropdown"]}
    >
      <Dropdown
        width="fit-content"
      >
        <DropdownButton
          renderAs={
            <Button
              size="sm"
              color="secondary"
              variant="outlined"
              leadingIcon={EllipsisVertical} />
          }
        />
        <DropdownMenu
          width="fit-content"
          horizontalPlacement="auto"
          verticalPlacement="auto"
        >
          {
            dropdownMenuItemsProps.map((item, i) =>
              <DropdownMenuItem
                key={`table-cell-dropdown-menu-item-${i}`}
                additionalData={additionalData}
                {...item}
              />
            )
          }
        </DropdownMenu>

      </Dropdown>
    </div>
      :
      <></>
  };

  return (
    <div
      className={styles["table-cell"]}
      role="cell"
      data-table-cell-selected={selected}
      data-table-cell-type={type}
      style={{ ...(width ? { width: width } : {}) }}
    >
      <div className={styles["table-cell__content"]}>
        {content}
      </div>
    </div>
  )
};